/**
 * Stakeholder Control Domain
 * 
 * Bounded area of decision authority for balancing stakeholder value.
 * Includes shareholder, employee, and community subdomains.
 * 
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md - "Stakeholder Control Domain" definition
 */

import React, { useState } from 'react';
import { Row, Col, Typography, Spin } from 'antd';
import {
  TeamOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useUser } from '../../contexts/UserContext';
import { useDashboardNavigation } from '../../contexts/DashboardNavigationContext';
import { 
  StakeholderValueComponent, 
  StakeholderValueDetailComponent 
} from '../../components/Dashboard/ReportingComponents';
import styles from './StakeholdersDashboard.module.css';

const { Title, Text } = Typography;

type StakeholderTypeKey = 'shareholder' | 'employees' | 'community';

// ============================================================================
// STAKEHOLDER CONTROL DOMAIN
// ============================================================================

export const StakeholdersDashboard: React.FC = () => {
  const { selectedCompany } = useUser();
  const { navigateTo } = useDashboardNavigation();
  
  // Track which stakeholder type is selected for detail expansion
  const [selectedStakeholderType, setSelectedStakeholderType] = useState<StakeholderTypeKey | null>(null);

  if (!selectedCompany) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
        <p>Loading stakeholder data...</p>
      </div>
    );
  }

  const handleStakeholderTypeSelect = (type: StakeholderTypeKey | null) => {
    setSelectedStakeholderType(type);
  };

  return (
    <div className={styles.dashboard}>
      {/* Control Domain Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <TeamOutlined className={styles.headerIcon} />
          <div>
            <Title level={2} className={styles.title}>Stakeholder Control</Title>
            <Text className={styles.subtitle}>
              Balance shareholder, employee, and community value
            </Text>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.content}>
        {/* Stakeholder Value Section */}
        <div className={styles.section}>
          <StakeholderValueComponent
            artifactId={selectedCompany.id}
            selectedStakeholderType={selectedStakeholderType}
            onStakeholderTypeSelect={handleStakeholderTypeSelect}
          />
          
          {/* Expandable Stakeholder Detail Section */}
          {selectedStakeholderType && (
            <div className={styles.stakeholderDetailSection}>
              <div className={styles.stakeholderDetailHeader}>
                <button 
                  className={styles.collapseButton}
                  onClick={() => setSelectedStakeholderType(null)}
                  aria-label="Collapse details"
                >
                  <UpOutlined /> Collapse Details
                </button>
              </div>
              <StakeholderValueDetailComponent
                artifactId={selectedCompany.id}
                lockedStakeholderType={selectedStakeholderType}
                showHeader={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StakeholdersDashboard;
